// Symulacja proby: staly krok czasu, faza osiadania, naped i warunki wyniku.

import * as planck from 'planck';
import type { ChallengeDefinition, DynamicProp, TrialOutcome } from '../challenges/step';
import { validateAll, validateForTrial, type ValidationIssue } from '../domain/validate';
import type { RobotBlueprint } from '../domain/types';
import {
  compileRobot,
  computeCenterOfMass,
  computeOrigin,
  robotBodies,
  robotBoundsWorld,
  type RobotHandles,
} from './compileRobot';

export interface TrialOptions {
  fixedDt: number;
  velocityIterations: number;
  positionIterations: number;
  /** Czas osiadania przy wylaczonym napedzie przed startem. */
  motorStartSec: number;
  sampleEverySec: number;
  /**
   * Mnoznik momentu hamowania po przekroczeniu mety (1 = pelny moment).
   * Za slabe hamowanie = robot przejezdza mete i spada z konca planszy.
   */
  brakeTorqueFactor: number;
}

export const DEFAULT_TRIAL_OPTIONS: TrialOptions = {
  fixedDt: 1 / 120,
  velocityIterations: 8,
  positionIterations: 3,
  motorStartSec: 0.4,
  sampleEverySec: 0.1,
  // 0,7 dobrano empirycznie: 0,3 zostawialo za malo hamowania (robot przejezdzal
  // mete i spadal z konca planszy), a pelny moment czesciej przewracal robota.
  brakeTorqueFactor: 0.7,
};

export interface TrialSample {
  t: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  comX: number;
  comY: number;
  angle: number;
  speed: number;
}

export interface TrialResult {
  outcome: Exclude<TrialOutcome, 'running'>;
  reason: string;
  elapsedSec: number;
  steps: number;
  final: TrialSample;
  samples: TrialSample[];
}

const TWO_PI = Math.PI * 2;

/**
 * Maksymalne przechylenie korpusu, przy ktorym uznajemy, ze pojazd "stoi
 * stabilnie" na mecie (~69 stopni). Pozy lezace/nosowe odrzucamy, ale pojazd
 * z roznymi kolami moze stac przechylony - to poprawna konstrukcja.
 */
const MAX_SETTLE_ANGLE = 1.2;

export function normalizeAngle(angle: number): number {
  let value = angle % TWO_PI;
  if (value > Math.PI) value -= TWO_PI;
  if (value < -Math.PI) value += TWO_PI;
  return value;
}

/** Uchwyt dynamicznej przeszkody: definicja + cialo w swiecie. */
export interface PropHandle {
  definition: DynamicProp;
  body: planck.Body;
}

function buildChallenge(world: planck.World, challenge: ChallengeDefinition): PropHandle[] {
  for (const box of challenge.staticBoxes) {
    const body = world.createBody({ type: 'static', position: new planck.Vec2(box.x, box.y) });
    body.createFixture({
      shape: new planck.Box(box.w / 2, box.h / 2),
      friction: challenge.groundFriction,
    });
  }

  // Dynamiczne przeszkody (skrzynki, kladki) - poruszaja sie w probie.
  const props: PropHandle[] = [];
  for (const prop of challenge.dynamicProps ?? []) {
    const body = world.createBody({
      type: 'dynamic',
      position: new planck.Vec2(prop.x, prop.y),
      linearDamping: 0.05,
      angularDamping: 0.2,
    });
    body.createFixture({
      shape: new planck.Box(prop.w / 2, prop.h / 2),
      // Masa = gestosc * powierzchnia (ten sam mechanizm co przy czesciach robota).
      density: prop.massKg / (prop.w * prop.h),
      friction: prop.friction ?? 0.7,
    });
    if (prop.pivot) {
      const anchor = new planck.Vec2(prop.x + prop.pivot.x, prop.y + prop.pivot.y);
      const frame = world.createBody({ type: 'static', position: anchor });
      world.createJoint(new planck.RevoluteJoint({}, frame, body, anchor));
    }
    props.push({ definition: prop, body });
  }
  return props;
}

export type CreateTrialResult =
  | { ok: true; trial: Trial }
  | { ok: false; issues: ValidationIssue[] };

export interface CreateTrialOptions {
  /**
   * Dopuszcza probe z NIEGOTOWA konstrukcja (brak akumulatora, napedu albo kol).
   * Blokuja wowczas tylko bledy strukturalne i budzet - pojazd co najwyzej nie
   * ruszy. Domyslnie false, zeby walidacja referencyjna pozostala ostra.
   */
  allowIncomplete?: boolean;
}

/** Buduje swiezy swiat dla proby. Bledy konstrukcji zwraca bez uruchamiania proby. */
export function createTrial(
  blueprint: RobotBlueprint,
  challenge: ChallengeDefinition,
  options: TrialOptions = DEFAULT_TRIAL_OPTIONS,
  validation: CreateTrialOptions = {},
): CreateTrialResult {
  const issues = validation.allowIncomplete
    ? validateForTrial(blueprint, challenge.budgetPln, challenge.limits)
    : validateAll(blueprint, challenge.budgetPln, challenge.limits);
  if (issues.length > 0) return { ok: false, issues };

  const world = new planck.World({
    gravity: new planck.Vec2(0, -10),
    allowSleep: false,
  });
  const props = buildChallenge(world, challenge);

  const origin = computeOrigin(blueprint, {
    startX: challenge.startX,
    groundTopY: challenge.groundTopY,
  });
  const handles = compileRobot(world, blueprint, origin);

  return { ok: true, trial: new Trial(world, handles, challenge, options, props) };
}

/** Pojedyncza proba: krok czasu, naped, hamowanie na mecie i ocena wyniku. */
export class Trial {
  private readonly world: planck.World;
  private readonly handles: RobotHandles;
  private readonly challenge: ChallengeDefinition;
  private readonly options: TrialOptions;
  private readonly propHandles: PropHandle[];

  elapsedSec = 0;
  steps = 0;
  outcome: TrialOutcome = 'running';
  reason = '';
  readonly samples: TrialSample[] = [];

  private sampleAccumulator = 0;
  private settleAccumulator = 0;
  private flipAccumulator = 0;
  private bestMinX = -Infinity;
  private lastProgressSec = 0;
  private motorOn = false;
  private braked = false;
  private current: TrialSample;

  constructor(
    world: planck.World,
    handles: RobotHandles,
    challenge: ChallengeDefinition,
    options: TrialOptions,
    props: PropHandle[] = [],
  ) {
    this.world = world;
    this.handles = handles;
    this.challenge = challenge;
    this.options = options;
    this.propHandles = props;
    this.current = this.captureSample();
    this.samples.push(this.current);
  }

  get sample(): TrialSample {
    return this.current;
  }

  get robot(): RobotHandles {
    return this.handles;
  }

  /** Dynamiczne przeszkody (do rysowania i oceny celu) - z aktualnymi pozycjami. */
  get props(): readonly PropHandle[] {
    return this.propHandles;
  }

  /** Jeden staly krok fizyki. Zwraca aktualny stan proby. */
  step(): TrialOutcome {
    if (this.outcome !== 'running') return this.outcome;
    const dt = this.options.fixedDt;
    this.elapsedSec += dt;
    this.steps += 1;

    if (!this.motorOn && this.elapsedSec >= this.options.motorStartSec) {
      this.motorOn = true;
      // Naped wlaczamy na WALACH SILNIKOW - moment trafia na kola przez przekladnie.
      for (const shaft of this.handles.shafts) {
        if (shaft.motor) shaft.joint.enableMotor(true);
      }
    }

    this.world.step(dt, this.options.velocityIterations, this.options.positionIterations);

    this.current = this.captureSample();
    this.sampleAccumulator += dt;
    if (this.sampleAccumulator >= this.options.sampleEverySec) {
      this.sampleAccumulator = 0;
      this.samples.push(this.current);
    }
    this.evaluate(this.current);
    return this.outcome;
  }

  /** Petla bez grafiki - do testow i strojenia. */
  runHeadless(maxSteps = 4000): TrialResult {
    let guard = 0;
    while (this.outcome === 'running' && guard < maxSteps) {
      this.step();
      guard += 1;
    }
    if (this.outcome === 'running') {
      this.finish('timeout', 'Przekroczono limit krokow symulacji.');
    }
    return this.toResult();
  }

  toResult(): TrialResult {
    return {
      outcome: this.outcome === 'running' ? 'timeout' : this.outcome,
      reason: this.reason,
      elapsedSec: this.elapsedSec,
      steps: this.steps,
      final: this.current,
      samples: this.samples,
    };
  }

  private captureSample(): TrialSample {
    const bounds = robotBoundsWorld(this.handles);
    const com = computeCenterOfMass(this.handles.chassis, robotBodies(this.handles));
    const velocity = this.handles.chassis.getLinearVelocity();
    return {
      t: this.elapsedSec,
      minX: bounds.minX,
      maxX: bounds.maxX,
      minY: bounds.minY,
      maxY: bounds.maxY,
      comX: com.x,
      comY: com.y,
      angle: normalizeAngle(this.handles.chassis.getAngle()),
      speed: Math.hypot(velocity.x, velocity.y),
    };
  }

  private evaluate(sample: TrialSample): void {
    const challenge = this.challenge;
    const dt = this.options.fixedDt;
    const oob = challenge.outOfBounds;

    if (
      sample.minX < oob.minX ||
      sample.maxX > oob.maxX ||
      sample.maxY > oob.maxY ||
      sample.minY < oob.minY
    ) {
      this.finish('out_of_bounds', 'Robot opuscil obszar proby.');
      return;
    }

    if (Math.abs(sample.angle) > 2.4) {
      this.flipAccumulator += dt;
      if (this.flipAccumulator >= 0.5) {
        this.finish('flipped', 'Robot przewrocil sie i nie wraca do jazdy.');
        return;
      }
    } else {
      this.flipAccumulator = 0;
    }

    // Po przekroczeniu mety hamujemy, by ocenic stabilne zatrzymanie.
    if (!this.braked && sample.minX >= challenge.finishX) {
      this.brake();
      this.braked = true;
    }

    const overLine = sample.minX >= challenge.finishX;
    const onPlatform = Math.abs(sample.minY - challenge.platformTopY) < 0.06;
    const slow = sample.speed < 0.15;
    // "Stoi stabilnie" NIE znaczy "poziomo": pojazd z roznymi kolami (np. ogromne
    // z tylu i male z przodu) stoi przechylony i to jest poprawna konstrukcja.
    // Odrzucamy tylko pozy lezace/nosowe - pelny obrot lapie osobny warunek 'flipped'.
    const settled = Math.abs(sample.angle) < MAX_SETTLE_ANGLE;
    const targetsMet = this.propTargetsMet();

    if (sample.minX > this.bestMinX + 0.01) {
      this.bestMinX = sample.minX;
      this.lastProgressSec = this.elapsedSec;
    }

    // "Zatrzymany" oceniamy tylko PRZED meta: po mecie robot ma prawo stac
    // (czeka na stabilny postoj), a nie dowieziony ladunek ma wlasny powod.
    const stalled =
      challenge.stuckTimeoutSec !== null &&
      this.motorOn &&
      this.elapsedSec - this.lastProgressSec > challenge.stuckTimeoutSec;
    if (stalled) {
      if (!overLine) {
        this.finish('stuck', 'Robot nie robi postepu od kilku sekund.');
        return;
      }
      if (!targetsMet) {
        this.finish('stuck', 'Robot minal mete, ale ladunek nie dotarl na mete.');
        return;
      }
    }

    if (overLine && onPlatform && slow && settled && targetsMet) {
      this.settleAccumulator += dt;
      if (this.settleAccumulator >= challenge.settleTimeSec) {
        const extra =
          (challenge.propTargets?.length ?? 0) > 0 ? ' Ladunek dotarl na mete.' : '';
        this.finish('won', `Cala konstrukcja przekroczyla mete i stoi stabilnie.${extra}`);
        return;
      }
    } else {
      this.settleAccumulator = 0;
    }

    if (this.elapsedSec >= challenge.timeLimitSec) {
      const missing = (challenge.propTargets?.length ?? 0) > 0 && !targetsMet;
      this.finish(
        'timeout',
        missing
          ? 'Przekroczono limit czasu - ladunek nie dotarl na mete.'
          : 'Przekroczono limit czasu proby.',
      );
    }
  }

  /** Czy wszystkie cele na przeszkodach sa spelnione (np. skrzynka za meta). */
  private propTargetsMet(): boolean {
    const targets = this.challenge.propTargets;
    if (!targets || targets.length === 0) return true;
    return targets.every((target) => {
      const prop = this.propHandles.find((entry) => entry.definition.id === target.propId);
      if (!prop) return true;
      const left = prop.body.getPosition().x - prop.definition.w / 2;
      return left >= target.minX;
    });
  }

  private brake(): void {
    // Hamujemy na walach silnikow - moment przejdzie na kola przez przekladnie.
    for (const shaft of this.handles.shafts) {
      if (!shaft.motor) continue;
      const torque = shaft.joint.getMaxMotorTorque();
      // Za slabe hamowanie = robot przejezdza mete i spada z konca planszy;
      // za mocne = przewraca sie do przodu. Sila dobrana empirycznie.
      shaft.joint.setMaxMotorTorque(torque * this.options.brakeTorqueFactor);
      shaft.joint.enableMotor(true);
      shaft.joint.setMotorSpeed(0);
    }
  }

  private finish(outcome: Exclude<TrialOutcome, 'running'>, reason: string): void {
    this.outcome = outcome;
    this.reason = reason;
  }
}

